import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomersService } from '../../services/customers.service';
import { CustomerModel, CustomerMergePreview } from '../../customer.model';

interface MergeCandidate {
  id: string;
  displayName: string;
  companyAccountNumber: string;
}

// Friendly labels for the backend count categories, in display order. Any
// category present in the preview but missing here falls back to its raw key.
const COUNT_LABELS: Array<{ key: string; label: string }> = [
  { key: 'paymentTokens', label: 'Payment accounts / tokens' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'invoiceEvents', label: 'Invoice history events' },
  { key: 'freelyPayments', label: 'Payments' },
  { key: 'scheduledPayments', label: 'Scheduled / autopay payments' },
  { key: 'schedulerJobs', label: 'Scheduler jobs' },
  { key: 'flashPayRequests', label: 'FlashPay requests' },
  { key: 'authLinks', label: 'Access links' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'registrationTokens', label: 'Registration tokens' },
  { key: 'sessions', label: 'Portal sessions' },
  { key: 'userProfiles', label: 'Portal users' },
  { key: 'selfOnboardRequests', label: 'Onboarding requests' },
];

/**
 * Modal body for "Migrate to alternate customer". Lets the user pick the
 * surviving customer, then shows a dry-run preview (per-category counts + net
 * result) of what will move before the merge is committed. The confirm button
 * is owned by the modal config (see config/actions.ts), which reads this
 * component's `selectedSurvivingId` / `canConfirm` state.
 */
@Component({
  standalone: true,
  selector: 'app-customer-migrate-confirm',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './customer-migrate-confirm.component.html',
  styleUrls: ['./customer-migrate-confirm.component.scss'],
  host: { class: 'app-customer-migrate-confirm' },
})
export class CustomerMigrateConfirmComponent implements OnInit {
  // The modal wrapper assigns the ModalData object; the config payload is at data.data.
  @Input() data: any;

  source!: CustomerModel;
  candidates: MergeCandidate[] = [];
  selectedSurvivingId: string | null = null;

  preview: CustomerMergePreview | null = null;
  previewLoading = false;
  previewError: string | null = null;

  readonly countLabels = COUNT_LABELS;

  constructor(private customerService: CustomersService) {}

  ngOnInit(): void {
    this.source = this.data?.data?.source as CustomerModel;
    this.loadCandidates();
  }

  private loadCandidates(): void {
    this.customerService.getCustomers().subscribe({
      next: (customers) => {
        this.candidates = customers
          .filter((c) => c.id && c.id !== this.source?.id)
          .map((c) => ({
            id: c.id!,
            displayName: c.displayName || c.companyName || '',
            companyAccountNumber: c.companyAccountNumber || '',
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
      },
      error: (error) => {
        this.previewError = 'Failed to load customers: ' + (error?.message ?? error);
      },
    });
  }

  onSurvivingSelected(): void {
    this.preview = null;
    this.previewError = null;
    if (!this.selectedSurvivingId || !this.source?.id) {
      return;
    }
    this.previewLoading = true;
    this.customerService
      .previewMergeCustomer(this.source.id, this.selectedSurvivingId)
      .subscribe({
        next: (preview) => {
          this.preview = preview;
          this.previewLoading = false;
        },
        error: (error) => {
          this.previewLoading = false;
          this.previewError =
            error?.error?.message || error?.message || 'Failed to generate merge preview';
        },
      });
  }

  /** Rows to render: labelled categories that have at least one record to move. */
  get movingRows(): Array<{ label: string; count: number }> {
    if (!this.preview) {
      return [];
    }
    const counts = this.preview.counts || {};
    const rows: Array<{ label: string; count: number }> = [];
    const seen = new Set<string>();
    for (const { key, label } of this.countLabels) {
      if (counts[key] > 0) {
        rows.push({ label, count: counts[key] });
      }
      seen.add(key);
    }
    // Any unexpected category the backend adds later still shows up.
    for (const key of Object.keys(counts)) {
      if (!seen.has(key) && counts[key] > 0) {
        rows.push({ label: key, count: counts[key] });
      }
    }
    return rows;
  }

  get survivingName(): string {
    return this.preview?.surviving?.displayName || this.preview?.surviving?.companyName || '';
  }

  get sourceName(): string {
    return this.source?.displayName || this.source?.companyName || '';
  }

  /** The confirm button (in the modal config) is enabled only when this is true. */
  get canConfirm(): boolean {
    return !!this.selectedSurvivingId && !!this.preview && !this.previewLoading;
  }
}
